import React, { useState, useEffect, useRef } from "react";
import QRCode from "qrcode";
import Modal from "./Modal";
import "./QRCodeModal.css";

interface QRCodeModalProps {
    isOpen: boolean;
    onClose: () => void;
    lotId: string;
    displayLotId: string;
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({ isOpen, onClose, lotId, displayLotId }) => {
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
    const [qrColor, setQrColor] = useState<string>("#000000");
    const [bgColor, setBgColor] = useState<string>("#FFFFFF");
    const [isLoading, setIsLoading] = useState(false);
    const [logoUrl, setLogoUrl] = useState<string>("");
    const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
    const [logoInputUrl, setLogoInputUrl] = useState('');
    const [logoError, setLogoError] = useState('');
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Track original colors for reset functionality
    const originalQrColor = "#000000";
    const originalBgColor = "#FFFFFF";
    const hasChanges = qrColor !== originalQrColor || bgColor !== originalBgColor;

    const generateQRCode = async () => {
        if (!lotId) return;

        setIsLoading(true);
        try {
            const url = `https://app.parkwithparallel.com/lotid/${displayLotId}`;

            const qrOptions = {
                errorCorrectionLevel: 'H' as const,
                margin: 1,
                width: 400,
                color: {
                    dark: qrColor,
                    light: bgColor
                }
            };

            const dataUrl = await QRCode.toDataURL(url, qrOptions);
            setQrCodeDataUrl(dataUrl);
        } catch (error) {
            console.error("Error generating QR code:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetColors = () => {
        setQrColor(originalQrColor);
        setBgColor(originalBgColor);
    };

    const handleAddLogo = () => {
        setLogoInputUrl('');
        setLogoError('');
        setIsLogoModalOpen(true);
    };

    const handleLogoSubmit = () => {
        if (!logoInputUrl.trim()) {
            setLogoError('Please enter a logo URL');
            return;
        }

        // Basic URL validation
        try {
            new URL(logoInputUrl);
        } catch {
            setLogoError('Please enter a valid URL');
            return;
        }

        // Check if it's an image file
        const validExtensions = ['.svg', '.png', '.jpg', '.jpeg', '.gif'];
        const hasValidExtension = validExtensions.some(ext =>
            logoInputUrl.toLowerCase().includes(ext)
        );

        if (!hasValidExtension) {
            setLogoError('Please enter a URL to an image file (SVG, PNG, JPEG, GIF)');
            return;
        }

        setLogoUrl(logoInputUrl);
        setIsLogoModalOpen(false);
        setLogoInputUrl('');
        setLogoError('');
    };

    const downloadQRCodeAsPNG = async () => {
        if (!qrCodeDataUrl) return;

        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;



                // Draw the QR code
                ctx.drawImage(img, 0, 0);

                // Create logo SVG with current color
                const logoSvg = `<svg width="48" height="48" viewBox="0 0 284 370" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.0966 369.63C2.97084 370.332 0 367.955 0 364.751V119.71C0 105.677 9.72866 93.5182 23.4205 90.4404L82.152 77.2381C85.2777 76.5354 88.2486 78.9126 88.2486 82.1163V177.376C88.2486 180.138 90.4872 182.376 93.2486 182.376H131.749C145.061 182.376 156.173 180.056 165.087 175.417C174.12 170.777 180.954 164.293 185.59 155.966C190.225 147.519 192.542 137.704 192.542 126.521C192.542 115.219 190.225 105.464 185.59 97.2555C180.954 88.9278 174.12 82.5036 165.087 77.9829C156.054 73.4621 144.942 71.2017 131.749 71.2017H109.042C109.037 71.2017 109.036 71.1947 109.041 71.1937C109.046 71.1926 109.045 71.1856 109.04 71.1856H5C2.23858 71.1856 0 68.9471 0 66.1856V5C0 2.23858 2.23858 0 5.00001 0H150.825C178.161 0 201.754 5.35353 221.602 16.0606C241.569 26.6486 256.961 41.46 267.777 60.4947C278.592 79.4104 284 101.419 284 126.521C284 151.742 278.473 173.811 267.42 192.726C256.486 211.523 240.856 226.097 220.532 236.447C200.208 246.797 176.081 251.972 148.151 251.972H93.2486C90.4872 251.972 88.2486 254.211 88.2486 256.972V327.158C88.2486 341.191 78.5199 353.349 64.8281 356.427L6.0966 369.63Z" fill="${qrColor}"/>
                </svg>`;

                let logoImgSrc: string;

                if (logoUrl) {
                    // Use custom logo URL directly
                    logoImgSrc = logoUrl;
                } else {
                    // Create SVG blob for default logo
                    const logoBlob = new Blob([logoSvg], { type: 'image/svg+xml' });
                    logoImgSrc = URL.createObjectURL(logoBlob);
                }

                const logoImg = new Image();
                logoImg.crossOrigin = 'anonymous';
                logoImg.onload = () => {
                    // Analyze QR code structure to find actual module size
                    const qrSize = canvas.width;

                    // Analyze QR code structure to find actual module size
                    const imageData = ctx.getImageData(0, 0, qrSize, qrSize);
                    const data = imageData.data;

                    // Find the QR code boundaries by scanning for black pixels
                    let minX = qrSize, minY = qrSize, maxX = 0, maxY = 0;

                    for (let y = 0; y < qrSize; y++) {
                        for (let x = 0; x < qrSize; x++) {
                            const index = (y * qrSize + x) * 4;
                            const r = data[index];
                            const g = data[index + 1];
                            const b = data[index + 2];

                            // Check if pixel is black (or very dark)
                            if (r < 128 && g < 128 && b < 128) {
                                minX = Math.min(minX, x);
                                minY = Math.min(minY, y);
                                maxX = Math.max(maxX, x);
                                maxY = Math.max(maxY, y);
                            }
                        }
                    }

                    // Calculate actual module size based on QR code dimensions
                    const qrModules = 21; // Standard QR code has 21x21 modules
                    const actualModuleSize = (maxX - minX) / (qrModules - 1);

                    // Calculate center position aligned with actual modules
                    const centerModule = Math.floor(qrModules / 2);
                    const centerX = Math.round(minX + (centerModule * actualModuleSize));
                    const centerY = Math.round(minY + (centerModule * actualModuleSize));

                    const logoSize = 48;

                    // Make background square align with actual module boundaries
                    const modulesForLogo = Math.ceil(logoSize / actualModuleSize);
                    const backgroundSize = modulesForLogo * actualModuleSize;
                    const backgroundOffset = backgroundSize / 2;

                    // Draw background square for logo
                    ctx.save();
                    ctx.fillStyle = bgColor;
                    ctx.fillRect(centerX - backgroundOffset, centerY - backgroundOffset, backgroundSize, backgroundSize);
                    ctx.restore();

                    // Draw the logo
                    ctx.drawImage(logoImg, centerX - logoSize / 2, centerY - logoSize / 2, logoSize, logoSize);

                    // Download the final image
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.download = `qr-code-lot-${displayLotId}.png`;
                            link.href = url;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                        }
                    }, bgColor === 'transparent' ? 'image/png' : 'image/png');

                    if (!logoUrl) {
                        URL.revokeObjectURL(logoImgSrc);
                    }
                };
                logoImg.src = logoImgSrc;
            };

            img.src = qrCodeDataUrl;
        } catch (error) {
            console.error("Error downloading QR code with logo:", error);
        }
    };

    useEffect(() => {
        if (isOpen && lotId) {
            generateQRCode();
        }
    }, [isOpen, lotId, qrColor, bgColor]);

    const downloadQRCode = () => {
        downloadQRCodeAsPNG();
    };

    const LogoComponent = ({ color }: { color: string }) => {
        if (logoUrl) {
            return (
                <img
                    src={logoUrl}
                    alt="Custom Logo"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        cursor: 'pointer'
                    }}
                    onClick={handleAddLogo}
                    onError={() => {
                        setLogoError('Failed to load image');
                        setLogoUrl('');
                    }}
                />
            );
        }

        return (
            <svg
                width="32"
                height="32"
                viewBox="0 0 284 370"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: '100%', height: '100%', cursor: 'pointer' }}
                onClick={handleAddLogo}
            >
                <path
                    d="M6.0966 369.63C2.97084 370.332 0 367.955 0 364.751V119.71C0 105.677 9.72866 93.5182 23.4205 90.4404L82.152 77.2381C85.2777 76.5354 88.2486 78.9126 88.2486 82.1163V177.376C88.2486 180.138 90.4872 182.376 93.2486 182.376H131.749C145.061 182.376 156.173 180.056 165.087 175.417C174.12 170.777 180.954 164.293 185.59 155.966C190.225 147.519 192.542 137.704 192.542 126.521C192.542 115.219 190.225 105.464 185.59 97.2555C180.954 88.9278 174.12 82.5036 165.087 77.9829C156.054 73.4621 144.942 71.2017 131.749 71.2017H109.042C109.037 71.2017 109.036 71.1947 109.041 71.1937C109.046 71.1926 109.045 71.1856 109.04 71.1856H5C2.23858 71.1856 0 68.9471 0 66.1856V5C0 2.23858 2.23858 0 5.00001 0H150.825C178.161 0 201.754 5.35353 221.602 16.0606C241.569 26.6486 256.961 41.46 267.777 60.4947C278.592 79.4104 284 101.419 284 126.521C284 151.742 278.473 173.811 267.42 192.726C256.486 211.523 240.856 226.097 220.532 236.447C200.208 246.797 176.081 251.972 148.151 251.972H93.2486C90.4872 251.972 88.2486 254.211 88.2486 256.972V327.158C88.2486 341.191 78.5199 353.349 64.8281 356.427L6.0966 369.63Z"
                    fill={color}
                />
            </svg>
        );
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                title="Download QR Code"
                confirmText="Download"
                cancelText="Cancel"
                onConfirm={downloadQRCode}
                onCancel={onClose}
                disableConfirm={!qrCodeDataUrl || isLoading}
            >
                <div className="qr-code-modal-content">
                    <div className="qr-code-controls">
                        <div className="color-control">
                            <input
                                id="qr-color"
                                type="color"
                                value={qrColor}
                                onChange={(e) => setQrColor(e.target.value)}
                                className="color-picker"
                            />
                        </div>
                        <div className="color-control">
                            <input
                                id="bg-color"
                                type="color"
                                value={bgColor}
                                onChange={(e) => setBgColor(e.target.value)}
                                className="color-picker"
                            />
                        </div>
                        {hasChanges && (
                            <button
                                type="button"
                                onClick={resetColors}
                                className="reset-btn"
                            >
                                Reset
                            </button>
                        )}
                    </div>

                    <div className="qr-code-preview">
                        {isLoading ? (
                            <div className="qr-loading">Generating QR Code...</div>
                        ) : qrCodeDataUrl ? (
                            <div className="qr-code-container">
                                <img
                                    src={qrCodeDataUrl}
                                    alt="QR Code"
                                    className="qr-code-image"
                                />
                                <div
                                    className="qr-logo-overlay"
                                    style={{
                                        backgroundColor: bgColor
                                    }}
                                >
                                    <LogoComponent color={qrColor} />
                                </div>
                            </div>
                        ) : (
                            <div className="qr-error">Failed to generate QR code</div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Logo URL Input Modal */}
            {isLogoModalOpen && (
                <div className="modal-overlay" onClick={() => setIsLogoModalOpen(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-close" onClick={() => setIsLogoModalOpen(false)}>
                            <span className="modal-cancel-text">Cancel</span>
                            <img src="/assets/x.svg" alt="Close" className="modal-close-icon" />
                        </div>

                        <h2>Add Logo</h2>
                        <div className="modal-content">
                            <p>Paste link to logo, must be an SVG, JPEG, PNG, or GIF:</p>
                            <input
                                type="text"
                                value={logoInputUrl}
                                onChange={(e) => setLogoInputUrl(e.target.value)}
                                placeholder="https://example.com/logo.png"
                                className="logo-url-input"
                            />
                            {logoError && <div className="error-message">{logoError}</div>}
                        </div>

                        <div className="modal-actions">
                            <button className="button-primary" onClick={handleLogoSubmit}>
                                Add Logo
                            </button>
                            <button className="button-secondary" onClick={() => setIsLogoModalOpen(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default QRCodeModal; 